import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  Row,
  Col,
  InputNumber,
  App,
} from 'antd';
import { createClosingRequest } from '../../../services/leadService';

const { TextArea } = Input;
const { Option } = Select;

interface LeadClosingRequestModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  lead: any;
  resubmitRequest?: any;
}

export const LeadClosingRequestModal: React.FC<LeadClosingRequestModalProps> = ({
  open,
  onCancel,
  onSuccess,
  lead,
  resubmitRequest,
}) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const [isSelfBuyer, setIsSelfBuyer] = useState(true);

  useEffect(() => {
    if (open) {
      if (resubmitRequest) {
        const isSelf = resubmitRequest.relationship_with_lead === 'Diri Sendiri';
        setIsSelfBuyer(isSelf);
        form.setFieldsValue({
          buyer_is_self: isSelf,
          buyer_name: resubmitRequest.buyer_name,
          buyer_phone: resubmitRequest.buyer_phone,
          buyer_email: resubmitRequest.buyer_email || '',
          buyer_nik: resubmitRequest.buyer_nik || '',
          buyer_npwp: resubmitRequest.buyer_npwp || '',
          relationship_with_lead: resubmitRequest.relationship_with_lead,
          purchased_property_detail: resubmitRequest.purchased_property_detail,
          omzet: resubmitRequest.omzet,
          supporting_documents: resubmitRequest.supporting_documents || '',
          notes: resubmitRequest.notes || '',
        });
      } else {
        setIsSelfBuyer(true);
        form.setFieldsValue({
          buyer_is_self: true,
          buyer_name: lead?.full_name || '',
          buyer_phone: lead?.phone || '',
          buyer_email: lead?.email || '',
          buyer_nik: '',
          buyer_npwp: '',
          relationship_with_lead: 'Diri Sendiri',
          purchased_property_detail: '',
          omzet: undefined,
          supporting_documents: '',
          notes: '',
        });
      }
    } else {
      form.resetFields();
    }
  }, [open, resubmitRequest, lead, form]);

  const handleValuesChange = (changedValues: any) => {
    if (changedValues.buyer_is_self !== undefined) {
      const isSelf = changedValues.buyer_is_self;
      setIsSelfBuyer(isSelf);
      if (isSelf) {
        form.setFieldsValue({
          buyer_name: lead?.full_name || '',
          buyer_phone: lead?.phone || '',
          buyer_email: lead?.email || '',
          relationship_with_lead: 'Diri Sendiri',
        });
      } else {
        form.setFieldsValue({
          relationship_with_lead: '',
        });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload = {
        lead_id: lead.id,
        buyer_name: values.buyer_name,
        buyer_phone: values.buyer_phone,
        buyer_email: values.buyer_email || undefined,
        buyer_nik: values.buyer_nik || undefined,
        buyer_npwp: values.buyer_npwp || undefined,
        relationship_with_lead: values.relationship_with_lead,
        purchased_property_detail: values.purchased_property_detail,
        omzet: Number(values.omzet),
        supporting_documents: values.supporting_documents || undefined,
        notes: values.notes || undefined,
      };

      const response = await createClosingRequest(payload);
      if (response.data?.status === 'success') {
        message.success('Pengajuan closing OK berhasil dikirim!');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.data?.message || 'Gagal mengirimkan pengajuan closing.');
      }
    } catch (error: any) {
      console.error('Submit Closing Request Failed:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Gagal mengirimkan pengajuan closing.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={resubmitRequest ? "Ajukan Ulang Closing OK" : "Ajukan Closing OK"}
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={handleSubmit}
      confirmLoading={submitting}
      width={650}
      okText="Kirim Pengajuan"
      cancelText="Batal"
      okButtonProps={{ style: { backgroundColor: '#7BAA18', borderColor: '#7BAA18' } }}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        initialValues={{
          buyer_is_self: true,
          relationship_with_lead: 'Diri Sendiri',
        }}
      >
        <Form.Item name="buyer_is_self" valuePropName="checked">
          <Checkbox>Pembeli adalah Lead itu sendiri</Checkbox>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Nama Pembeli (sesuai KTP/SPR)"
              name="buyer_name"
              rules={[{ required: true, message: 'Nama pembeli wajib diisi' }]}
            >
              <Input placeholder="Nama lengkap pembeli" disabled={isSelfBuyer} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="No. Telepon Pembeli"
              name="buyer_phone"
              rules={[{ required: true, message: 'Nomor telepon wajib diisi' }]}
            >
              <Input placeholder="Contoh: 0812345678" disabled={isSelfBuyer} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Email Pembeli"
              name="buyer_email"
              rules={[{ type: 'email', message: 'Format email tidak valid' }]}
            >
              <Input placeholder="Email pembeli (opsional)" disabled={isSelfBuyer} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Hubungan dengan Lead"
              name="relationship_with_lead"
              rules={[{ required: true, message: 'Hubungan dengan lead wajib diisi' }]}
            >
              <Select placeholder="Pilih hubungan..." disabled={isSelfBuyer}>
                <Option value="Diri Sendiri">Diri Sendiri</Option>
                <Option value="Suami">Suami</Option>
                <Option value="Istri">Istri</Option>
                <Option value="Anak">Anak</Option>
                <Option value="Orang Tua">Orang Tua</Option>
                <Option value="Perwakilan Perusahaan">Perwakilan Perusahaan</Option>
                <Option value="Lainnya">Lainnya</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="NIK KTP Pembeli"
              name="buyer_nik"
            >
              <Input placeholder="16 digit NIK (opsional)" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="NPWP Pembeli"
              name="buyer_npwp"
            >
              <Input placeholder="Format NPWP (opsional)" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Detail Unit Properti yang Dibeli"
          name="purchased_property_detail"
          rules={[{ required: true, message: 'Detail unit properti wajib diisi' }]}
        >
          <Input placeholder="Contoh: Cluster Roseville Blok C No 11" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Nominal Omzet (Deal Transaksi Riil)"
              name="omzet"
              rules={[{ required: true, message: 'Nominal omzet wajib diisi' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Contoh: 1500000000"
                formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value ? value.replace(/Rp\s?|(,*)/g, '') : ''}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Link Dokumen Pendukung (Folder Drive)"
              name="supporting_documents"
              rules={[{ type: 'url', message: 'Format URL wajib valid' }]}
            >
              <Input placeholder="https://drive.google.com/..." />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Catatan Tambahan"
          name="notes"
        >
          <TextArea rows={3} placeholder="Catatan tambahan (opsional)..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LeadClosingRequestModal;
